import { Project } from '@arra/core';
import { Check, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button as MuiButton,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import useProjects from '../project/context/useProjects';
import keyify from '../project/parameters/keyify';
import TextBlock from '../results/TextBlock';
import Button from '../ui/Button';
import useReports from './context/useReports';
import Markdown from '../ui/Markdown';

enum Step {
  INTRO = 0,
  NAME_ENTRY = 1,
  PROJECT_SELECTION = 2,
  MULTI_YEAR_WARNING = 3,
}

interface CreateReportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose?: any;
}

const validationSchema = yup.object({
  name: yup.string().required('Champ obligatoire'),
  projects: yup.array().required('Champ obligatoire'),
});

const CreateReportModal = ({
  open,
  setOpen,
  onClose,
}: CreateReportModalProps) => {
  const { t } = useTranslation(['annual-reports', 'common', 'model']);
  const [step, setStep] = useState<Step>(Step.INTRO);
  const navigate = useNavigate();
  const called = useRef(false);
  const autofocusFirst = useRef(true);
  const { create } = useReports();
  const { projects } = useProjects({ finalized: true });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>(
    {},
  );

  const groupedProjects = useMemo(() => {
    const groups: Record<string, Project[]> = {};
    projects?.forEach((project) => {
      const key = project.year?.toString() || 'unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(project);
    });

    const entries = Object.entries(groups);
    const sorted = entries.sort(([a], [b]) => {
      const aNum = isNaN(+a) ? null : +a;
      const bNum = isNaN(+b) ? null : +b;

      if (aNum === null && bNum === null) return 0;
      if (aNum === null) return -1;
      if (bNum === null) return 1;

      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });
    return sorted;
  }, [projects, sortDirection]);

  const formik = useFormik<{
    name: string;
    projects: string[];
  }>({
    initialValues: {
      name: '',
      projects: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      // STEP 1: Name entry -> Project selection
      if (step === Step.NAME_ENTRY) {
        setStep(Step.PROJECT_SELECTION);
        return;
      }

      const selectedYears = new Set(
        values.projects.map((id) => {
          const project = projects?.find((p) => p._id.toString() === id);
          return project?.year ?? 'unknown';
        }),
      );

      // STEP 2: Project selection -> Multi-year warning (if needed)
      if (step === Step.PROJECT_SELECTION && selectedYears.size > 1) {
        setStep(Step.MULTI_YEAR_WARNING);
        return;
      }

      // STEP 3: Confirm & create (or single-year selection)
      if (step === Step.MULTI_YEAR_WARNING || selectedYears.size <= 1) {
        const result = await create({
          name: values.name,
          projectIds: values.projects,
        });
        handleClose();
        navigate(`/annual-reports/${result._id}`);
      }
    },
  });

  const toggleAllInYear = (year: string, selectAll: boolean) => {
    const yearProjects = groupedProjects.find(([y]) => y === year)?.[1] ?? [];
    const projectIds = yearProjects.map((p) => p._id.toString());
    const newSelection = selectAll
      ? [...new Set([...formik.values.projects, ...projectIds])]
      : formik.values.projects.filter((id) => !projectIds.includes(id));
    formik.setFieldValue('projects', newSelection);
  };

  const handleClose = () => {
    called.current = false;
    autofocusFirst.current = true;
    onClose && onClose();
    setOpen(false);
    setStep(Step.INTRO);
  };

  return (
    <Dialog
      maxWidth={step === Step.PROJECT_SELECTION ? 'lg' : 'sm'}
      open={open}
      onClose={handleClose}
      scroll="paper"
      id="create_modal"
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{t('annual-reports:creation-modal.title')}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">

            {step === Step.INTRO ? (
              // STEP 0: Intro (explanation only)
              <Markdown>{t('annual-reports:creation-modal.explanation')}</Markdown>
            ) : step === Step.NAME_ENTRY ? (
              // STEP 1: Name entry
              <>
                <TextField
                  id="name"
                  name="name"
                  label={t('annual-reports:creation-modal.name')}
                  autoFocus
                  fullWidth
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </>
            ) : step === Step.PROJECT_SELECTION ? (
              // STEP 2: Project selection
              <>
                <Alert
                  severity="info"
                  action={
                    <Button
                      color="secondary"
                      size="small"
                      onClick={() => navigate('/')}
                    >
                      {t('annual-reports:creation-modal.go-to-projects')}
                    </Button>
                  }
                >
                  {t('annual-reports:creation-modal.only-finalized-projects')}
                </Alert>
                <Box
                  mt={2}
                  mb={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <FormLabel>
                    {t('annual-reports:creation-modal.projects-selector')}
                  </FormLabel>

                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight={500}>
                      {t('annual-reports:creation-modal.sort.label')}
                    </Typography>
                    <MuiButton
                      variant="outlined"
                      size="small"
                      sx={{ textTransform: 'none' }}
                      onClick={() =>
                        setSortDirection((prev) =>
                          prev === 'asc' ? 'desc' : 'asc',
                        )
                      }
                    >
                      {t(
                        `annual-reports:creation-modal.sort.${sortDirection === 'asc' ? 'desc' : 'asc'
                        }`,
                      )}
                    </MuiButton>
                  </Box>
                </Box>
                <Box display="flex" flexDirection={'column'} gap={2}>
                  {groupedProjects.map(([year, yearProjects]) => {
                    const isExpanded = expandedYears[year] ?? true;
                    return (
                      <Box
                        key={year}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          p: 2,
                          bgcolor: '#f5f5f5',
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          bgcolor="#e0e0e0"
                          px={2}
                          py={1}
                          mb={1}
                        >
                          <Typography variant="h6">
                            {year === 'unknown'
                              ? t('annual-reports:creation-modal.no-year')
                              : year}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <MuiButton
                              size="small"
                              onClick={() => toggleAllInYear(year, true)}
                            >
                              {t('common:select-all')}
                            </MuiButton>
                            <MuiButton
                              size="small"
                              onClick={() => toggleAllInYear(year, false)}
                            >
                              {t('common:deselect-all')}
                            </MuiButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedYears((prev) => ({
                                  ...prev,
                                  [year]: !isExpanded,
                                }))
                              }
                            >
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Box>
                        </Box>
                        {isExpanded && (
                          <FormGroup>
                            {yearProjects.map((project) => {
                              const isChecked = formik.values.projects.includes(
                                project._id.toString(),
                              );
                              const singularName = t(
                                `model:${keyify(
                                  project.model.name,
                                )}.singularName`,
                              );
                              return (
                                <FormControlLabel
                                  key={project._id.toString()}
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={formik.handleChange}
                                      value={project._id.toString()}
                                      name="projects"
                                    />
                                  }
                                  label={
                                    <Box
                                      display="flex"
                                      flexDirection="row"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{ whiteSpace: 'nowrap' }}
                                      >
                                        {project.name}
                                      </Typography>
                                      {project.finalizedAt && (
                                        <Check fontSize="small" />
                                      )}
                                      <Chip
                                        label={`${project.completionRate}%`}
                                        variant="outlined"
                                        size="small"
                                      />
                                      <Chip
                                        label={singularName}
                                        size="small"
                                        sx={{
                                          backgroundColor: project.model.color,
                                          color: 'black',
                                        }}
                                      />
                                    </Box>
                                  }
                                />
                              );
                            })}
                          </FormGroup>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </>
            ) : (
              // STEP 3: Multi-year warning
              <>
                <Alert severity="warning">
                  {t('annual-reports:creation-modal.multiple-years-warning')}
                </Alert>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ position: 'sticky', bottom: 0, background: 'white' }}
        >
          {step > Step.INTRO && (
            <Button onClick={() => setStep((s) => Math.max(Step.INTRO, s - 1))} color="secondary">
              {t('common:back')}
            </Button>
          )}
          <Button onClick={handleClose} color="secondary">
            {t('common:cancel')}
          </Button>
          {step === Step.INTRO ? (
            <Button type="button" color="primary" onClick={() => setStep(Step.NAME_ENTRY)}>
              {t('common:next')}
            </Button>
          ) : (
            <Button type="submit" color="primary">
              {step === Step.NAME_ENTRY
                ? t('common:next')
                : step === Step.PROJECT_SELECTION
                  ? t('annual-reports:creation-modal.save')
                  : t('common:confirm')}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateReportModal;
